import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { BsX, BsTruck } from "react-icons/bs";
import UnifiedCartOrderConfirmModal from "../UnifiedCartOrderConfirmModal";
import { useOrderCreateAPI } from "@/app/hooks/api_hooks/HDCart/useOrderCreateAPI";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { IHDCartCheckoutPostBody, IHDCartOrderCreationFilter } from "@/api_framework/api_modals/Cart";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useHDCartCheckoutAPI } from "@/app/hooks/api_hooks/HDCart/useHDCartCheckoutAPI";
import toast from "react-hot-toast";
import { ICartCheckout, useCartCheckoutAPI } from "@/app/hooks/api_hooks/Cart/useCartCheckoutAPI";

export interface ICheckoutProps {
  phone_number: string;
}

export default function UnifiedCartCheckoutModal({
  open,
  setOpen,
  currentAddress,
  activeCartDetails,
  setActiveModal,
  activeCartId,
  mappedClinicId,
  availableHdItems,
  handlePresent,
  callLogAPIBase,
  selectedParavetId,
}) {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { execute: fetchOrderApi } = useOrderCreateAPI();
  const configuration = useConfiguration();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId, customerDetails } = routeState.state as ICustomerProfileState;
  const { execute: cartCheckout } = useHDCartCheckoutAPI();
  const { execute: checkoutOrder } = useCartCheckoutAPI();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cartCheckoutKey, setCartCheckoutKey] = useState<string | null>(null);

  const generateCartKey = useCallback(
    () =>
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    [],
  );

  useEffect(() => {
    if (!activeCartId) {
      setCartCheckoutKey(null);
      return;
    }
    const storageKey = `hd_cart_checkout_key_${activeCartId}`;
    let storedKey = sessionStorage.getItem(storageKey);
    if (!storedKey) {
      storedKey = generateCartKey();
      sessionStorage.setItem(storageKey, storedKey);
    }
    setCartCheckoutKey(storedKey);
  }, [activeCartId, generateCartKey]);

  const handleConfirmClick = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const hasNormalCart = Array.isArray(activeCartDetails?.pets) && activeCartDetails?.pets.length > 0;
    const hasHDCart = Array.isArray(availableHdItems) && availableHdItems.length > 0;
    try {
      if (hasNormalCart) {
        const constructedBody: ICartCheckout = {
          cart_id: activeCartId,
          clinic_id: mappedClinicId,
          ...(selectedParavetId ? { paravet_id: selectedParavetId } : {}),
        };
        await checkoutOrder(constructedBody);
      }
      if (hasHDCart) {
        const razorpayEnv = configuration.env.mode === "development" ? "dev" : "prod";
        const checkoutKey = cartCheckoutKey || generateCartKey();
        if (!cartCheckoutKey && activeCartId) {
          const storageKey = `hd_cart_checkout_key_${activeCartId}`;
          sessionStorage.setItem(storageKey, checkoutKey);
          setCartCheckoutKey(checkoutKey);
        }
        const payload: IHDCartOrderCreationFilter = {
          user_id: customerId,
          created_by: customerDetails?.email,
          address_id: currentAddress?.address_id,
          razorpay_env: razorpayEnv,
          pincode: currentAddress?.pincode,
          final_price: activeCartDetails?.price_breakup?.home_delivery_total,
          payment_partner: "razorpay",
          source: "clinic",
          source_clinic: mappedClinicId,
          idemp_key: checkoutKey,
        };
        if (activeCartDetails?.booking_id) {
          payload.appointment_id = activeCartDetails.booking_id;
        }
        const body: IHDCartCheckoutPostBody = {
          cart_id: activeCartDetails?.hd_cart?.hd_cart_id,
        };
        const paymentMethodId = 2;
        const [orderRes, checkoutRes] = await Promise.all([
          fetchOrderApi(payload, paymentMethodId),
          cartCheckout(body),
        ]);
        if (orderRes?.status && checkoutRes?.status) {
          sessionStorage.setItem("pendingOrderData", JSON.stringify(orderRes.data));
          toast.success("COD selected. Order placed successfully!");
        } else {
          if (!orderRes?.status) toast.error("Order creation failed, please try again.");
          if (!checkoutRes?.status) toast.error("Cart checkout failed, please try again.");
        }
      }
      setOpen(false);
      setTimeout(() => setConfirmModalOpen(true), 300);
    } catch (error) {
      console.error("Checkout failed:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={() => { }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4 sm:w-[500px] sm:p-6 flex flex-col">
                  <div className="pb-2 flex justify-between items-center">
                    <div className="font-semibold text-sm">Checkout Confirmation</div>
                    <button
                      type="button"
                      className="text-black bg-[#808DA01A] rounded-full"
                      onClick={() => setOpen(false)}
                      ref={cancelButtonRef}
                    >
                      <BsX className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="mt-2 rounded-md text-sm">Confirm with the client whether the address is correct</div>
                  <div className="rounded-lg bg-[#f4f4f4] p-3 mt-5">
                    <div className="flex gap-2 items-center">
                      <div className="font-medium text-sm">Delivery Address</div>
                      <div className="rounded-2xl py-1 px-3 text-[10px] font-medium">{currentAddress?.label}</div>
                      {currentAddress?.previously_ordered && (
                        <div className="rounded-md bg-[#dedede] text-[#666666] w-24 p-1 text-[10px] text-center">
                          Last Delivered
                        </div>
                      )}
                    </div>
                    <div className="text-[#6a717e] text-xs mt-3">{currentAddress?.address_1}</div>
                    <div className="text-[#6a717e] text-xs mt-1">{currentAddress?.address_2}</div>
                    <div className="flex gap-2 items-center mt-2">
                      <BsTruck className="w-5 h-5 text-black" />
                      <div className="text-[#6a717e] text-xs">
                        Delivered by{" "}
                        <span className="text-black font-medium">
                          {activeCartDetails?.price_breakup?.delivery_status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-5">
                      <button
                        type="button"
                        className="rounded-lg w-full px-10 py-[9px] text-sm shadow-sm text-[#0069fe] bg-white font-medium"
                        onClick={() => {
                          setOpen(false);
                          setActiveModal("addressList");
                        }}
                      >
                        Change Address
                      </button>
                    </div>
                  </div>
                  <div className="mt-5">
                    <button
                      type="button"
                      className="rounded-md w-full px-10 py-[9px] font-normal text-sm shadow-sm bg-green-700 text-white cursor-pointer"
                      onClick={handleConfirmClick}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Processing..." : "Confirm"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <UnifiedCartOrderConfirmModal
        open={confirmModalOpen}
        setOpen={setConfirmModalOpen}
        activeCartDetails={activeCartDetails}
        currentAddress={currentAddress}
        handlePresent={handlePresent}
        callLogAPIBase={callLogAPIBase}
      />
    </>
  );
}
